import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { carTestLabels } from "@/i18n";

const DASHBOARD_ISSUES = [
  "Varningslampor",
  "Hastighetsmätare",
  "Bränslemätare",
  "Temperatur",
  "Annat",
] as const;

interface DashboardSectionProps {
  dashboard: { problem?: string; issue?: string; other?: string } | null;
  onDashboardChange: (dashboard: {
    problem?: string;
    issue?: string;
    other?: string;
  }) => void;
  onNext: () => void;
}

export const DashboardSection = ({
  dashboard,
  onDashboardChange,
  onNext,
}: DashboardSectionProps) => {
  const [otherText, setOtherText] = useState(dashboard?.other || "");
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const form = ct.form ?? {};
  const v = ct.pdf?.values ?? ({} as Record<string, string>);
  const opt =
    (v as { optionValues?: Record<string, string> }).optionValues ?? {};

  const handleResultChange = (problem: string) => {
    if (problem === "no") {
      onDashboardChange({ problem });
    } else {
      onDashboardChange({ problem, issue: undefined, other: undefined });
    }
  };

  const handleIssueChange = (issue: string) => {
    if (issue === "Annat") {
      onDashboardChange({ ...dashboard, issue, other: otherText });
    } else {
      onDashboardChange({ ...dashboard, issue, other: undefined });
    }
  };

  const handleOtherTextChange = (text: string) => {
    setOtherText(text);
    if (dashboard?.issue === "Annat") {
      onDashboardChange({ ...dashboard, other: text });
    }
  };

  const showNextButton =
    dashboard?.problem === "yes" &&
    dashboard?.issue === "Annat" &&
    otherText.trim();

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-medium text-sm mb-3">{form.questionDashboard}</h4>
        <div className="grid grid-cols-2 gap-1.5 mb-4">
          <Button
            onClick={() => handleResultChange("yes")}
            variant="formOption"
            className={`${
              dashboard?.problem === "yes"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.yes}
          </Button>
          <Button
            onClick={() => handleResultChange("no")}
            variant="formOption"
            className={`${
              dashboard?.problem === "no"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.no}
          </Button>
        </div>

        {dashboard?.problem === "yes" && (
          <div className="space-y-3">
            <h5 className="font-medium text-sm">{form.questionWhichProblem}</h5>
            <div className="grid grid-cols-2 gap-1.5">
              {DASHBOARD_ISSUES.map((issue) => (
                <Button
                  key={issue}
                  onClick={() => handleIssueChange(issue)}
                  variant="formOption"
                  className={`${
                    dashboard?.issue === issue
                      ? "border-accent border-2"
                      : "border-gray-400"
                  }`}
                >
                  {opt[issue] ?? issue}
                </Button>
              ))}
            </div>

            {dashboard?.issue === "Annat" && (
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder={form.placeholderDescribeProblem}
                  value={otherText}
                  onChange={(e) => handleOtherTextChange(e.target.value)}
                  className="w-full"
                />
              </div>
            )}
          </div>
        )}

        {showNextButton && (
          <Button className="w-full mt-4" onClick={onNext}>
            {form.next}
          </Button>
        )}
      </div>
    </div>
  );
};
